import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { cx } from '../lib/cx'
import { Icon, type IconName } from './Icon'

/**
 * Botón del panel.
 *
 * El primario va en `--accent-text` y no en `--accent` ni en `--accent-deep`: el
 * blanco sobre el verde de marca da 2,21:1 y sobre `--accent-deep` 3,17:1.
 * Sobre `--accent-text` llega a 5,32:1, que es lo que pide la spec. El verde de
 * marca sigue estando en el panel como relleno, punto, barra y borde.
 */
export type ButtonVariante =
  | 'primario'
  | 'secundario'
  | 'sutil'
  | 'chico'
  | 'peligro'

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variante?: ButtonVariante
  /** Icono a la izquierda del texto. Nunca reemplaza al texto. */
  icono?: IconName
  /** Icono a la derecha, para "abre en otro sistema" o un chevron. */
  iconoDerecha?: IconName
  /** Ocupa el ancho del contenedor. */
  ancho?: boolean
  /** Deshabilita y muestra que está en curso. */
  cargando?: boolean
  children?: ReactNode
}

const BASE =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-semibold transition-colors duration-150 ease-brand disabled:cursor-not-allowed disabled:opacity-50'

const VARIANTE: Record<ButtonVariante, string> = {
  primario:
    'h-7 rounded-md border border-transparent bg-accent-text px-3 text-meta text-white hover:bg-[#086A01] active:bg-[#075E01]',
  secundario:
    'h-7 rounded-md border border-border bg-surface px-3 text-meta text-text hover:border-n300 hover:bg-n100',
  sutil:
    'h-7 rounded-md border border-transparent bg-transparent px-2 text-meta text-muted-strong hover:bg-n100 hover:text-text',
  // El chip de acción de una fila o de un KPI vacío. Se destaca por peso de
  // borde y no por color: cargar un dato que falta no es un crítico de negocio.
  chico:
    'h-5 rounded border border-border bg-surface px-1.5 text-micro text-text hover:border-accent hover:bg-accent-soft hover:text-accent-text',
  peligro:
    'h-7 rounded-md border border-danger bg-danger-soft px-3 text-meta text-danger-text hover:bg-surface',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variante = 'secundario',
      icono,
      iconoDerecha,
      ancho,
      cargando,
      children,
      className,
      disabled,
      type = 'button',
      ...rest
    },
    ref,
  ) {
    const tamañoIcono = variante === 'chico' ? 10 : 13

    return (
      <button
        {...rest}
        ref={ref}
        type={type}
        disabled={disabled === true || cargando === true}
        aria-busy={cargando === true ? true : undefined}
        data-variante={variante}
        className={cx(BASE, VARIANTE[variante], ancho === true && 'w-full', className)}
      >
        {icono !== undefined ? <Icon name={icono} size={tamañoIcono} /> : null}
        {children}
        {iconoDerecha !== undefined ? (
          <Icon name={iconoDerecha} size={tamañoIcono} />
        ) : null}
      </button>
    )
  },
)
